import { FC, MouseEvent, ReactNode } from 'react';
import { graphql, useFragment, useRelayEnvironment } from 'react-relay';
import { usePrerenderedCheckout } from 'dibs-checkout/exports/prerender/usePrerenderedCheckout';

// components
import { FormattedMessage } from 'dibs-react-intl';
import { Button, ButtonProps } from 'dibs-elements/exports/Button';

// mutations
import { SharedModifyPortfolioItems } from '../../mutations/FindingSharedModifyPortfolioItems';

// helpers
import serverVars from 'server-vars';
import { redirectToOMT } from './utils';
import { Maybe } from 'dibs-ts-utils/exports/Maybe';
import { useAppDispatch } from '../hooks/useReactReduxHooks';
import { trackPurchaseClick, Location } from '../tracking/actionButtonsTracking';
import { setCartItems } from '../../actions/sharedMobileActions';
import { hidePage } from 'dibs-buyer-layout/exports/hidePage';

import { PdpSharedPurchaseButton_item$key } from './__generated__/PdpSharedPurchaseButton_item.graphql';

// styles
import styles from './PdpSharedButtons.scss';

const forcedCurrency = serverVars.get('forcedCurrency');

const itemFragment = graphql`
    fragment PdpSharedPurchaseButton_item on Item
    @argumentDefinitions(pageDisplayEnum: { type: "PageDisplayEnum", defaultValue: pdp }) {
        isPrivateVisibilityType
        lastActiveTransaction(buyerId: $userId) {
            serviceId
            hasCompletedCheckout
        }
        hasPrivateListing
        # used in trackPurchaseClick
        # eslint-disable-next-line relay/unused-fields
        pdpTrackingVpvParams(
            userId: $userId
            userCountryCode: $userCountryCode
            userZipCode: $userZipCode
            fdfQuoteABTestVariant: $fdfQuoteABTestVariant
        ) @include(if: $afterDisplay)
        ...actionButtonsTracking_item
            @arguments(fetchAuction: $fetchAuction, pageDisplayEnum: $pageDisplayEnum)
    }
`;

type Props = {
    item: PdpSharedPurchaseButton_item$key | null | undefined;
    quantitySelected?: string;
    itemId: string;
    userId?: Maybe<string>;
    skuId?: string;
    label?: ReactNode;
    size?: 'small' | 'medium' | 'large';
    isPrimary?: boolean;
    disabled?: boolean;
    hasPrivateOffer?: Maybe<boolean>;
    location?: Location;
};

const purchaseCta = (
    <FormattedMessage id="PdpSharedPurchaseButton.purchaseCta" defaultMessage="Purchase" />
);

const acceptOfferCta = (
    <FormattedMessage id="PdpSharedPurchaseButton.acceptOfferCta" defaultMessage="Accept Offer" />
);

//here
export const PdpSharedPurchaseButton: FC<Props> = ({
    item: itemRef,
    quantitySelected,
    itemId,
    userId,
    skuId,
    label,
    size = 'large',
    isPrimary = true,
    disabled = false,
    hasPrivateOffer = false,
    location,
}) => {
    const environment = useRelayEnvironment();
    const dispatch = useAppDispatch();

    const item = useFragment(itemFragment, itemRef);

    const { redirect: redirectToCheckout } = usePrerenderedCheckout({
        hideParentPage: hidePage,
        itemId,
        params: {
            skuId,
            quantity: quantitySelected,
            offerCurrency: forcedCurrency,
            acceptOffer: hasPrivateOffer ? 'true' : undefined,
        },
    });

    const addToCart = (): void => {
        if (!userId) {
            // If no user, store item - it can be ADDed to cart when user logs in later
            dispatch(setCartItems(itemId));
        } else {
            // ADD item to user's cart
            SharedModifyPortfolioItems.commit(environment, {
                action: 'ADD',
                itemId,
                portfolioType: 'CART',
                userId,
            });
        }
    };

    const onClick = (event?: MouseEvent): void => {
        const { serviceId: transactionId, hasCompletedCheckout } =
            item?.lastActiveTransaction || {};

        const hasPrivateListing = item?.hasPrivateListing;
        trackPurchaseClick(item, event, location, hasPrivateOffer);

        if (!hasPrivateListing) {
            addToCart();
        }

        if (hasPrivateOffer && transactionId && hasCompletedCheckout) {
            redirectToOMT(transactionId);
        } else {
            redirectToCheckout();
        }
    };

    const ctaCopy = hasPrivateOffer ? acceptOfferCta : purchaseCta;
    const isPrivateVisibilityType = item?.isPrivateVisibilityType;
    const additionalProps: Partial<ButtonProps> = {};
    if (isPrivateVisibilityType) {
        additionalProps.type = 'primaryAlt';
    } else if (!isPrimary) {
        additionalProps.type = 'secondary';
    }

    return (
        <Button
            className={`${styles.button} ${styles.fullWidth}`}
            dataTn="item-actions-purchase-item"
            onClick={onClick}
            size={size}
            disabled={disabled}
            {...additionalProps}
        >
            {label ? <>{label}</> : ctaCopy}
        </Button>
    );
};
